import { useState, useRef, useEffect, useCallback } from 'react';

import ButtonWithBubble from '@components/Button/ButtonWithBubble';

import { calculate } from '../calculations';
import { DEFAULT_TURNOVER, GBP_TO_USD, MIN_SALARY, VALIDATION } from '../constants';
import {
  type WorkforceType,
  type CurrencyType,
  type CalculationResult,
  type CalculatorInputs,
} from '../types';
import { CalculatorForm } from './CalculatorForm';
import { CalculatorResults } from './CalculatorResults';
import styles from '../style.module.scss';

interface CalculatorAppProps {
  eyebrow: string;
  heading: string;
  description: string;
  heroBody: string;
  formSubheading: string;
  guideUrl: string;
  guideImageUrl: string;
  guideImageAlt: string;
  guideBackImageUrl: string;
  guideCardHeading: string;
  resultLabel: string;
  breakdownCtaHeading: string;
  breakdownCtaBody: string;
  guideCtaText: string;
  popupHeading: string;
  popupBody: string;
  country: string;
}

function trackEvent(event: string, data: Record<string, unknown> = {}) {
  try {
    const dl = (window as unknown as Record<string, unknown>).dataLayer;
    if (Array.isArray(dl)) {
      dl.push({ event, page_name: document.title, ...data });
    }
  } catch {
    /* noop */
  }
}

function detectCurrency(country: string): CurrencyType {
  // 1. Server-side geolocation from WP Engine (most reliable)
  if (country === 'US') return 'usd';
  if (country && country !== 'US') return 'gbp';

  // 2. Fallback: browser locale
  try {
    if (navigator.language.startsWith('en-US')) return 'usd';
  } catch {
    /* noop */
  }

  return 'gbp';
}

export function CalculatorApp({
  eyebrow,
  heading,
  description,
  heroBody,
  formSubheading,
  guideUrl,
  guideImageUrl,
  guideImageAlt,
  guideBackImageUrl,
  guideCardHeading,
  resultLabel,
  breakdownCtaHeading: _breakdownCtaHeading,
  breakdownCtaBody: _breakdownCtaBody,
  guideCtaText,
  popupHeading,
  popupBody,
  country,
}: CalculatorAppProps) {
  const [workforceType, setWorkforceType] = useState<WorkforceType | null>(null);
  const [totalEmployees, setTotalEmployees] = useState('');
  const [averageSalary, setAverageSalary] = useState('');
  const [turnoverRate, setTurnoverRate] = useState('');
  const [currency, setCurrency] = useState<CurrencyType>(() => detectCurrency(country));
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [resultKey, setResultKey] = useState(0);
  const [scrollKey, setScrollKey] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState(false);
  const hasCalculated = useRef(false);
  const modalTimer = useRef<ReturnType<typeof setTimeout>>();
  const modalShown = useRef(false);

  const closeModal = useCallback(() => {
    setShowModal(false);
    clearTimeout(modalTimer.current);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Trigger when cursor reaches the nav area (top 140px) or leaves the page
      if (e.clientY <= 140 && hasCalculated.current && !modalShown.current) {
        setShowModal(true);
        modalShown.current = true;
        trackEvent('ee_calculator_guide_modal_shown', { trigger: 'exit_intent' });
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleEscape);

    return () => {
      clearTimeout(modalTimer.current);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleWorkforceChange = (type: WorkforceType) => {
    setWorkforceType(type);
    if (!turnoverRate) {
      setTurnoverRate(String(DEFAULT_TURNOVER[type]));
    }
    setResult(null);
    setErrors((prev) => {
      const { workforceType: _, ...rest } = prev;
      return rest;
    });
    trackEvent('ee_calculator_workforce_selected', { workforce_type: type });
  };

  const handleCalculate = () => {
    const employees = parseInt(totalEmployees.replace(/,/g, ''), 10);
    const salary = parseInt(averageSalary.replace(/,/g, ''), 10);
    const turnover = parseFloat(turnoverRate);
    const newErrors: Record<string, string> = {};

    if (!workforceType) {
      newErrors.workforceType = 'required';
    }

    if (
      !employees ||
      employees < VALIDATION.employees.min ||
      employees > VALIDATION.employees.max
    ) {
      newErrors.totalEmployees = 'Enter a number between 100 and 500,000';
    }

    const minSal = MIN_SALARY[currency];
    if (!salary || salary < minSal) {
      newErrors.averageSalary = `Minimum ${currency === 'gbp' ? '£15,000' : '$20,000'}`;
    }

    if (!turnover || turnover < VALIDATION.turnover.min) {
      newErrors.turnoverRate = 'Enter a rate of at least 1%';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const inputs: CalculatorInputs = {
      workforceType: workforceType!,
      totalEmployees: employees,
      averageSalary: salary,
      turnoverRate: turnover,
      currency,
    };

    const calcResult = calculate(inputs);
    const isFirstCalculation = !hasCalculated.current;
    setResult(calcResult);
    if (isFirstCalculation) {
      setResultKey((k) => k + 1);
    }
    setScrollKey((k) => k + 1);
    hasCalculated.current = true;

    // Show guide modal after first calculation
    if (!modalShown.current) {
      clearTimeout(modalTimer.current);
      modalTimer.current = setTimeout(() => {
        setShowModal(true);
        modalShown.current = true;
        trackEvent('ee_calculator_guide_modal_shown', { trigger: 'timer' });
      }, 10000);
    }

    trackEvent('ee_calculator_results_displayed', {
      workforce_type: workforceType,
      total_employees: employees,
      total_cost: Math.round(calcResult.totalCost),
      avoidable_saving: Math.round(calcResult.avoidableSaving),
      currency,
    });
  };

  return (
    <>
      {/* Plum calculator section */}
      <div className={styles.calculator}>
        <div className={styles.heroRow}>
          <div className={styles.headerContent}>
            <p className={styles.eyebrow}>{eyebrow}</p>
            <h1 className={styles.heading} dangerouslySetInnerHTML={{ __html: heading }} />
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
            {heroBody && (
              <div className={styles.heroBody} dangerouslySetInnerHTML={{ __html: heroBody }} />
            )}
          </div>

          <div className={styles.formWrapper}>
            <CalculatorForm
              formSubheading={formSubheading}
              workforceType={workforceType}
              totalEmployees={totalEmployees}
              averageSalary={averageSalary}
              turnoverRate={turnoverRate}
              currency={currency}
              errors={errors}
              onWorkforceChange={handleWorkforceChange}
              onTotalEmployeesChange={setTotalEmployees}
              onAverageSalaryChange={setAverageSalary}
              onTurnoverRateChange={setTurnoverRate}
              onCurrencyChange={(newCurrency) => {
                let newSalary = averageSalary;
                if (averageSalary && currency !== newCurrency) {
                  const salaryNum = Number(averageSalary);
                  if (salaryNum > 0) {
                    const converted =
                      newCurrency === 'usd'
                        ? Math.round(salaryNum * GBP_TO_USD)
                        : Math.round(salaryNum / GBP_TO_USD);
                    if (Number.isFinite(converted) && converted > 0) {
                      newSalary = String(converted);
                      setAverageSalary(newSalary);
                    }
                  }
                }
                setCurrency(newCurrency);
                // Recalculate with new currency + converted salary
                if (hasCalculated.current && workforceType) {
                  const employees = parseInt(totalEmployees.replace(/,/g, ''), 10);
                  const salary = parseInt(newSalary.replace(/,/g, ''), 10);
                  const turnover = parseFloat(turnoverRate);
                  if (employees && salary && turnover) {
                    setResult(
                      calculate({
                        workforceType,
                        totalEmployees: employees,
                        averageSalary: salary,
                        turnoverRate: turnover,
                        currency: newCurrency,
                      }),
                    );
                  }
                }
              }}
              onCalculate={handleCalculate}
            />
          </div>
        </div>
      </div>

      {result && (
        <CalculatorResults
          key={resultKey}
          scrollKey={scrollKey}
          result={result}
          currency={currency}
          totalEmployees={parseInt(totalEmployees.replace(/,/g, ''), 10)}
          turnoverRate={parseFloat(turnoverRate)}
          resultLabel={resultLabel}
          guideUrl={guideUrl}
          guideImageUrl={guideImageUrl}
          guideImageAlt={guideImageAlt}
          guideBackImageUrl={guideBackImageUrl}
          guideCardHeading={guideCardHeading}
          guideCtaText={guideCtaText}
          onGuideClick={() =>
            trackEvent('ee_calculator_guide_clicked', {
              workforce_type: workforceType,
            })
          }
        />
      )}

      {/* Guide popup modal */}
      {showModal && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          className={styles.guideModal}
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Download guide"
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className={styles.guideModalContent} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.guideModalClose}
              onClick={closeModal}
              aria-label="Close"
              autoFocus
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className={styles.guideModalBody}>
              {popupHeading && (
                <p
                  className={styles.guideModalHeading}
                  dangerouslySetInnerHTML={{ __html: popupHeading }}
                />
              )}
              {popupBody && (
                <p
                  className={styles.guideModalText}
                  dangerouslySetInnerHTML={{ __html: popupBody }}
                />
              )}
              <ButtonWithBubble
                url={guideUrl}
                variant="primary"
                className={styles.calcButton}
                onClick={() => {
                  trackEvent('ee_calculator_guide_modal_clicked', {
                    workforce_type: workforceType,
                  });
                  closeModal();
                }}
              >
                Download the full guide
              </ButtonWithBubble>
            </div>
            {(guideImageUrl || guideBackImageUrl) && (
              <div className={styles.guideModalBooks}>
                {guideBackImageUrl && (
                  <div className={styles.guideModalBookSide}>
                    <img src={guideBackImageUrl} alt="" />
                  </div>
                )}
                {guideImageUrl && (
                  <div className={styles.guideModalBookCenter}>
                    <img src={guideImageUrl} alt={guideImageAlt || ''} />
                  </div>
                )}
                {guideBackImageUrl && (
                  <div className={styles.guideModalBookSide}>
                    <img src={guideBackImageUrl} alt="" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
